import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import {
  getLeasingProvider,
  isLeasingQualifiedCountry,
} from '@internal/utils/machine/leasing'
import { Machine } from '@internal/utils/machine/Machine'
import { UserContext } from '@internal/utils/user/UserContext'
import {
  ProductCard,
  ProductServiceCard,
  ServiceProps,
} from '@renderer-ui-library/molecules'
import { Grid } from '@renderer-ui-library/mui'
import { LeasingQualifiedContext } from '@renderer-ui-library/organisms'
import React, { useContext, useMemo } from 'react'
import { useMachineUrl } from 'utils/routing/hooks/useMachineUrl'

interface Props {
  services: ServiceProps[] | null
  machines: Machine[]
  locale: TLocale
}

export const ResultsList: React.FC<Props> = React.memo((props) => {
  const { getMachineUrl } = useMachineUrl()
  const { isLeasingQualifiedEnabled } = useContext(LeasingQualifiedContext)

  const { user } = useContext(UserContext)
  const leasingAvailable = isLeasingQualifiedCountry(user.countryCode)
  const leasingProvider = getLeasingProvider(user.countryCode)

  type Entry =
    | { type: 'machine'; machine: Machine }
    | { type: 'service'; service: ServiceProps }

  const entries: Entry[] = useMemo(() => {
    const list: Entry[] = (
      isLeasingQualifiedEnabled && user.countryCode
        ? props.machines.filter((m) => !!m.financing[user.countryCode!])
        : props.machines
    ).map((machine) => ({
      type: 'machine',
      machine,
    }))

    const services: ServiceProps[] = props.services ?? []
    services.forEach((service) => {
      if (service.serviceType === 'leasing' && !leasingAvailable) {
        return
      }
      if (service.repeat && service.repeatPosition) {
        let index = 1
        let position = service.initialPosition - 1

        while (position < list.length) {
          list.splice(position, 0, {
            type: 'service',
            service,
          })
          position =
            service.repeatPosition * index + service.initialPosition - 1
          index += 1
        }
      } else {
        list.splice(service.initialPosition - 1, 0, {
          type: 'service',
          service,
        })
      }
    })

    return list
  }, [
    isLeasingQualifiedEnabled,
    leasingAvailable,
    props.machines,
    props.services,
    user.countryCode,
  ])

  return (
    <>
      {entries.map((entry, index) => (
        <Grid
          key={
            entry.type === 'machine'
              ? entry.machine.machineGroupId
              : `service-${index}-${entry.service.serviceType}-${entry.service.title}`
          }
          item
          xs={12}
          md={6}
          lg={4}
        >
          {entry.type === 'machine' ? (
            <ProductCard
              title={entry.machine.name}
              highlightData={{
                power: entry.machine.enginePower,
                year: entry.machine.yearOfProduction,
                hours: entry.machine.engineHours,
              }}
              url={getMachineUrl(props.locale, entry.machine)}
              locale={props.locale}
              machine={entry.machine}
            />
          ) : (
            <ProductServiceCard
              {...entry.service}
              leasingProvider={leasingProvider}
              leasingAvailable={leasingAvailable}
            />
          )}
        </Grid>
      ))}
    </>
  )
})

ResultsList.displayName = 'ResultsList'
